import client from './client'
import type { User, IndustryFocus } from '../types'

export interface ChangelogEntry {
  id: string
  title: string
  version: string
  category: 'feature' | 'improvement' | 'fix' | 'security' | 'launch'
  description: string
  published_at: string
}

export interface BlogPostSummary {
  id: string
  title: string
  slug: string
  author_name: string
  author_avatar: string
  excerpt: string
  cover_image_url: string
  category: string
  tags: string[]
  published_at: string
  read_time_minutes: number
}

export interface BlogPost extends BlogPostSummary {
  content: string
  content_type: string
  view_count: number
  seo_title: string
  seo_description: string
  updated_at: string
}

export interface ReferralCodeData {
  code: string
  referral_url: string
  times_used: number
  total_credit_earned: string
  pending_credits: string
  available_credits: string
}

export async function register(username: string, email: string, password: string) {
  const { data } = await client.post('/api/auth/register/', { username, email, password })
  return data as { access: string; refresh: string; user: User }
}

export async function login(username: string, password: string) {
  const { data } = await client.post('/api/token/', { username, password })
  localStorage.setItem('access_token', data.access)
  localStorage.setItem('refresh_token', data.refresh)
  return data as { access: string; refresh: string }
}

export async function me() {
  const { data } = await client.get('/api/auth/me/')
  return data as User
}

export async function onboarding(payload: {
  industry: string
  business_name: string
  business_description?: string
  primary_goal?: string
  selected_focuses?: string[]
  team_size?: string
}) {
  const { data } = await client.post('/api/auth/onboarding/', payload)
  // Response carries the updated user plus a seeded first-chat task
  // (starter_agent_id / starter_prompt) used to route into an agent chat.
  return data as User & { starter_agent_id?: string; starter_prompt?: string }
}

export async function getFocuses(industry?: string): Promise<IndustryFocus[]> {
  const params = industry ? `?industry=${encodeURIComponent(industry)}` : ''
  const { data } = await client.get(`/api/onboarding/focuses/${params}`)
  return data as IndustryFocus[]
}

export async function forgotPassword(email: string) {
  const { data } = await client.post('/api/auth/forgot-password/', { email })
  return data as { message: string }
}

export async function validateResetToken(token: string) {
  const { data } = await client.get(`/api/auth/validate-reset-token/${token}/`)
  return data as { valid: boolean }
}

export async function resetPassword(token: string, new_password: string) {
  const { data } = await client.post('/api/auth/reset-password/', { token, new_password })
  return data as { message: string }
}

export async function googleAuth(credential: string) {
  const { data } = await client.post('/api/auth/google/', { credential })
  return data as { access: string; refresh: string; user: User; is_new_user: boolean }
}

export async function setPassword(new_password: string) {
  const { data } = await client.post('/api/auth/set-password/', { new_password })
  return data as { message: string }
}

export async function getProfile(): Promise<User> {
  const { data } = await client.get('/api/auth/profile/')
  return data as User
}

export async function updateProfile(payload: Partial<User>) {
  const { data } = await client.patch('/api/auth/profile/', payload)
  return data as User
}

export async function changePassword(current_password: string, new_password: string) {
  const refresh_token = localStorage.getItem('refresh_token')
  const { data } = await client.post('/api/auth/change-password/', {
    current_password,
    new_password,
    ...(refresh_token ? { refresh_token } : {}),
  })
  // Backend issues fresh tokens after password change — store them
  if (data.access) localStorage.setItem('access_token', data.access)
  if (data.refresh) localStorage.setItem('refresh_token', data.refresh)
  return data as { message: string; access?: string; refresh?: string }
}

export async function validateInviteCode(code: string) {
  const { data } = await client.post('/api/auth/validate-invite-code/', { code })
  return data as { valid: boolean; label?: string; grants_industry_agents?: boolean; error?: string }
}

export async function applyInviteCode(code: string) {
  const { data } = await client.post('/api/auth/apply-invite-code/', { code })
  return data as { success: boolean; message: string; expires_at: string | null; grants_industry_agents: boolean }
}

export async function logout() {
  const refresh_token = localStorage.getItem('refresh_token')
  try {
    await client.post('/api/auth/logout/', { refresh_token })
  } catch {
    // Continue with local logout even if the API call fails
  }
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
}

export async function getBanner(): Promise<{ enabled: boolean; message: string; version: number }> {
  const { data } = await client.get('/api/site/banner/')
  return data
}

export async function submitContact(payload: {
  name: string
  email: string
  subject: string
  message: string
  honeypot: string
}) {
  const { data } = await client.post('/api/contact/', payload)
  return data as { success: boolean; message: string }
}

export async function getChangelog(params?: { category?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams()
  if (params?.category) query.set('category', params.category)
  if (params?.limit) query.set('limit', String(params.limit))
  if (params?.offset) query.set('offset', String(params.offset))
  const { data } = await client.get(`/api/changelog/?${query}`)
  return data as ChangelogEntry[]
}

export async function getBlogPosts(params?: { category?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams()
  if (params?.category) query.set('category', params.category)
  if (params?.limit) query.set('limit', String(params.limit))
  if (params?.offset) query.set('offset', String(params.offset))
  const { data } = await client.get(`/api/blog/?${query}`)
  return data as { results: BlogPostSummary[]; total: number; has_more: boolean }
}

export async function getBlogPost(slug: string) {
  const { data } = await client.get(`/api/blog/${slug}/`)
  return data as BlogPost
}

export async function getReferralCode() {
  const { data } = await client.get('/api/referral/my-code/')
  return data as ReferralCodeData
}

export async function validateReferralCode(code: string) {
  const { data } = await client.get(`/api/referral/validate/?code=${encodeURIComponent(code)}`)
  return data as { valid: boolean; discount_percent?: number; error?: string }
}
